import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";
import { LatinArpeggioSection } from "./PianoIntermediate27Latin";

/**
 * Piano Intermediate · Lesson 27 (second half) — the La La Land arpeggio on the ii-V-I.
 *
 * Source: the lesson video (transcript PI23_Rhythm_Patterns_Latin_Arpeggio.txt,
 * checked against a fresh whisper pass of video 27) and Jason's handwritten pages.
 * The video does NOT teach the song City of Stars: it brings back "the City of
 * Stars rhythm from the earlier video, the La La Land rhythm" and plays the 2-5-1
 * with it, root–fifth toggle in the left hand. His chart writes the arpeggio as
 * one bar per chord — 1-3-5-7, an eighth rest, 7-5-3 (Dm7: D F A C' · C' A F),
 * swing feel — and gives four endings for the last bar of the 2-5-1:
 * Cmaj7 held · Cmaj6 (C-E-G-A) · A7 (C♯-E-G-A) · A7♭9 (C♯-E-G-B♭ over A).
 *
 * Bespoke visual: CityOfStarsMap (kept from the earlier page) — one card per
 * ending, four chord boxes each. Non-diatonic chords (A7, A7♭9) get a rose accent.
 */

type SectionId = "hold" | "six" | "a7" | "a7b9";

interface ChordDef {
  name: string;
  /** Root in semitones above the tonic, and the chord-symbol suffix (for any key). */
  root: number;
  suffix: string;
  /** The four arpeggio notes, semitones above the tonic, low → high. */
  offsets: number[];
  /** Rose accent for non-diatonic chords */
  spice?: boolean;
}

interface SongSection {
  id: SectionId;
  label: string;
  barCount: string;
  chords: [ChordDef, ChordDef, ChordDef, ChordDef];
}

// Semitones above the tonic (C in the video).
const DM7: ChordDef   = { name: "Dm7",   root: 2, suffix: "m7",   offsets: [2, 5, 9, 12] };   // D F A C'
const G7: ChordDef    = { name: "G7",    root: 7, suffix: "7",    offsets: [-5, -1, 2, 5] };  // G B D F
const CMAJ7: ChordDef = { name: "Cmaj7", root: 0, suffix: "maj7", offsets: [0, 4, 7, 11] };   // C E G B
const CMAJ6: ChordDef = { name: "C6",    root: 0, suffix: "6",    offsets: [0, 4, 7, 9] };    // C E G A
const A7: ChordDef    = { name: "A7",    root: 9, suffix: "7",    offsets: [1, 4, 7, 9], spice: true };    // C♯ E G A
const A7B9: ChordDef  = { name: "A7♭9",  root: 9, suffix: "7♭9",  offsets: [1, 4, 7, 10], spice: true };   // C♯ E G B♭, A in the bass

const SECTIONS: SongSection[] = [
  { id: "hold", label: "Stay on the 1",          barCount: "4 bars", chords: [DM7, G7, CMAJ7, CMAJ7] },
  { id: "six",  label: "Slide down to the 6th",  barCount: "4 bars", chords: [DM7, G7, CMAJ7, CMAJ6] },
  { id: "a7",   label: "Squeeze in A7",          barCount: "4 bars", chords: [DM7, G7, CMAJ7, A7] },
  { id: "a7b9", label: "A7 with a flat 9",       barCount: "4 bars", chords: [DM7, G7, CMAJ7, A7B9] },
];

/** The La La Land arpeggio, one bar per chord: 1-3-5-7, rest, 7-5-3 (index into offsets). */
const LALA_CELLS: (number | null)[] = [0, 1, 2, 3, null, 3, 2, 1];

export default function PianoIntermediate27() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "27");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      {/* Video 27 teaches the ii-V-I in three rhythm patterns — blocks, the Latin
          rhythm, then the La La Land arpeggio. Both halves of the companion live here,
          in the video's order. */}
      <LatinArpeggioSection />
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="The La La Land Arpeggio on the ii-V-I"
          exerciseGoal="The third rhythm pattern: the City of Stars arpeggio over the 2-5-1, with a root–fifth left hand and four ways to end the phrase."
          bpm={92}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);

  const [bpm, setBpm] = useState(92);
  const [activeSection, setActiveSection] = useState<SectionId | "full" | null>(null);
  const [activeChordIdx, setActiveChordIdx] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  useEffect(() => {
    if (activeSection !== null) setRhythmBpm(bpm, 0.05);
  }, [bpm, activeSection]);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setActiveSection(null);
    setActiveChordIdx(-1);
  };

  const chordLabel = (c: ChordDef) => k.nameForPc((k.tonicPc + c.root) % 12) + c.suffix;
  const sectionsForKey: SongSection[] = SECTIONS.map((sec) => ({
    ...sec,
    chords: sec.chords.map((c) => ({ ...c, name: chordLabel(c) })) as SongSection["chords"],
  }));

  /** One bar per chord: the arpeggio in the right hand, root then fifth in the left. */
  const barEvents = (chord: ChordDef, bar: number) => {
    const events: { beat: number; notes: string[]; durationSteps: number; velocity: number }[] = [];
    LALA_CELLS.forEach((cell, j) => {
      if (cell == null) return;
      events.push({
        beat: bar * 8 + j,
        notes: [midiToPitchName(tonicMidi + chord.offsets[cell], k.useFlats)],
        durationSteps: 1,
        velocity: j === 0 ? 0.85 : 0.7,
      });
    });
    const bassRoot = tonicMidi - 12 + chord.root - (chord.root > 6 ? 12 : 0);
    events.push({ beat: bar * 8, notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 4, velocity: 0.65 });
    events.push({ beat: bar * 8 + 4, notes: [midiToPitchName(bassRoot + 7, k.useFlats)], durationSteps: 4, velocity: 0.55 });
    return events;
  };

  const playSection = async (sectionId: SectionId) => {
    kickAudio();
    stop();
    setActiveSection(sectionId);
    const section = SECTIONS.find((sec) => sec.id === sectionId)!;
    const events = section.chords.flatMap((chord, i) => barEvents(chord, i));
    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 2,
      totalSteps: 32,
      loop: true,
      click: false,
      beatsPerBar: 4,
      onStep: (st) => setActiveChordIdx(Math.floor(st / 8) % 4),
    });
  };

  const playFullSong = async () => {
    kickAudio();
    stop();
    setActiveSection("full");
    const all = SECTIONS.flatMap((sec) => sec.chords);
    const events = all.flatMap((chord, i) => barEvents(chord, i));
    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 2,
      totalSteps: all.length * 8,
      loop: true,
      click: false,
      beatsPerBar: 4,
      onStep: (st) => setActiveChordIdx(Math.floor(st / 8)),
    });
  };

  // For a card, determine if a particular chord at position i is active
  const isChordActive = (sectionId: SectionId, chordPos: number): boolean => {
    if (activeSection === sectionId) return activeChordIdx === chordPos;
    if (activeSection === "full") {
      const sectionStart = SECTIONS.findIndex((sec) => sec.id === sectionId) * 4;
      return activeChordIdx === sectionStart + chordPos;
    }
    return false;
  };

  const spell = (c: ChordDef) =>
    c.offsets.map((o) => k.nameForPc(((k.tonicPc + o) % 12 + 12) % 12)).join(" ");

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <p className="text-[12.5px] text-cyan-300/70">
          Jason plays it in C. The key picker moves the chord names and the audio together.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The third pattern" title="The La La Land arpeggio">
          <p>
            The last rhythm of the lesson is the arpeggio from the previous video — the one inspired
            by <em>City of Stars</em> in La La Land. One bar per chord: the 1, 3, 5 and 7 going up,
            an eighth rest, then 7, 5, 3 coming down. On {chordLabel(DM7)}:{" "}
            <strong>{spell(DM7)} · rest · {spell(DM7).split(" ").slice(1).reverse().join(" ")}</strong>.
            Give it a swing feel.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Root and fifth toggle in the left always brings out that groove."
          </PIQuote>

          {/* The spine callout */}
          <div className="my-4 rounded-2xl border border-cyan-400/30 bg-cyan-950/40 p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">
              The 2-5-1
            </div>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[14px] font-bold">
              <span className="rounded-lg bg-cyan-500/20 px-2.5 py-1 text-cyan-200">{chordLabel(DM7)}</span>
              <span className="text-cyan-400/60">→</span>
              <span className="rounded-lg bg-cyan-500/20 px-2.5 py-1 text-cyan-200">{chordLabel(G7)}</span>
              <span className="text-cyan-400/60">→</span>
              <span className="rounded-lg bg-amber-500/20 px-2.5 py-1 text-amber-200">{chordLabel(CMAJ7)}</span>
            </div>
            <p className="mt-2 text-[12px] text-cyan-200/80">
              One bar each on the 2 and the 5, two bars on the 1. That extra bar on the 1 is where
              the four endings below come in.
            </p>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Four endings" title="Make the extra bar more interesting">
          <CityOfStarsMap
            sections={sectionsForKey}
            activeSection={activeSection}
            isChordActive={isChordActive}
            onPlaySection={playSection}
            activePlayingSection={activeSection}
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={playFullSong}
              disabled={activeSection !== null}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Play all four (loop)
            </button>
            {activeSection !== null && (
              <button
                onClick={stop}
                className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20"
              >
                ■ Stop
              </button>
            )}
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input
                type="range" min={72} max={110} step={1} value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
                className="h-1 w-32 accent-amber-400"
              />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>
          <p className="mt-3 text-[13px] text-cyan-300/80">
            From Jason's chart: hold the {chordLabel(CMAJ7)}; slide it down to {chordLabel(CMAJ6)} (
            {spell(CMAJ6)}); squeeze in {chordLabel(A7)} ({spell(A7)}), the secondary dominant of{" "}
            {chordLabel(DM7).replace("m7", " minor")}; or give that A7 a flat 9 ({spell(A7B9)} over{" "}
            {k.nameForPc((k.tonicPc + 9) % 12)} in the bass).
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The A7♭9" title="A secondary dominant with a flat 9">
          <p>
            The A7 is the <strong>dominant of {chordLabel(DM7).replace("m7", " minor")}</strong> — the
            5 of the 2 — so it leads straight back to the top of the 2-5-1. Its major 3rd (
            {k.nameForPc((k.tonicPc + 1) % 12)}) is not in the key of {k.tonicName}; that's what makes
            it a secondary dominant.
          </p>
          <p className="mt-2 text-[14px] text-cyan-50/95">
            In C, the <strong>♭9</strong> is a B♭ — a semitone above the root A, but played an
            octave higher, which is why it is called a flat <em>ninth</em> and not a flat second.
            Underneath it the A7 keeps its C♯ and its G — and those two are a tritone apart.
          </p>
          <div className="my-3 rounded-2xl border border-rose-400/30 bg-rose-900/20 px-4 py-3">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-rose-300">
              A7♭9 in C — Jason's voicing
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { note: "A", role: "root — left hand" },
                { note: "C♯", role: "M3" },
                { note: "E", role: "5th" },
                { note: "G", role: "♭7 — tritone with the C♯" },
                { note: "B♭", role: "♭9" },
              ].map(({ note, role }, i) => (
                <div
                  key={i}
                  className={`flex flex-col items-center rounded-lg px-3 py-2 ${
                    i === 4
                      ? "border border-rose-400/60 bg-rose-500/20"
                      : "border border-zinc-600 bg-zinc-800"
                  }`}
                >
                  <span className="font-mono text-base font-bold text-white">{note}</span>
                  <span className="text-[10px] text-cyan-300/70">{role}</span>
                </div>
              ))}
            </div>
          </div>
          <PIInsight>
            <p>
              Or play the plain 2-5-1 and just stay on the 1. Then take all four endings to F and G.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Practice ladder" title="Twenty minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Get the arpeggio under your fingers on one chord before you chain the 2-5-1, and add
                the endings last.
              </>
            }
            rungs={[
              {
                title: "One chord, one bar",
                minutes: 5,
                accent: "amber",
                detail: "1-3-5-7, rest, 7-5-3 on the 2 chord alone. Swing the eighths. Then on the 5 and the 1.",
              },
              {
                title: "Add the left hand",
                minutes: 5,
                accent: "cyan",
                detail: "Root on beat 1, fifth on beat 3, under every chord. That toggle brings out the groove.",
              },
              {
                title: "The whole 2-5-1",
                minutes: 5,
                accent: "rose",
                detail: "Two, five, one, one — a bar each, without stopping between chords.",
              },
              {
                title: "The four endings",
                minutes: 5,
                accent: "emerald",
                detail: "Hold the 1, slide to the 6th, squeeze in the A7, then the A7♭9. Then F and G.",
              },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: CityOfStarsMap ────────────────────────────────── */
function CityOfStarsMap({
  sections,
  activeSection,
  isChordActive,
  onPlaySection,
  activePlayingSection,
}: {
  sections: SongSection[];
  activeSection: SectionId | "full" | null;
  isChordActive: (sectionId: SectionId, chordPos: number) => boolean;
  onPlaySection: (id: SectionId) => void;
  activePlayingSection: SectionId | "full" | null;
}) {
  return (
    <div className="my-4 space-y-3">
      {sections.map((section) => {
        const isSectionPlaying =
          activePlayingSection === section.id ||
          (activePlayingSection === "full");

        return (
          <div
            key={section.id}
            className={`rounded-2xl border p-3 transition-all ${
              isSectionPlaying && activePlayingSection === section.id
                ? "border-amber-400/50 bg-zinc-900/70"
                : "border-cyan-400/20 bg-zinc-900/30"
            }`}
          >
            {/* Section header */}
            <div className="mb-2 flex items-center justify-between">
              <div>
                <span className="text-[13px] font-bold text-cyan-100">{section.label}</span>
                <span className="ml-2 text-[11px] text-cyan-400/60">{section.barCount}</span>
              </div>
              <button
                onClick={() => onPlaySection(section.id)}
                disabled={activePlayingSection !== null}
                className={`rounded-lg border px-3 py-1 text-[11px] font-bold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
                  activePlayingSection === section.id
                    ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                    : "border-cyan-400/20 text-cyan-300/70 hover:border-cyan-300/40 hover:bg-cyan-500/8 hover:text-cyan-200"
                }`}
              >
                {activePlayingSection === section.id ? "▶ Playing" : "▶ Play"}
              </button>
            </div>

            {/* 4 chord boxes */}
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
            >
              {section.chords.map((chord, i) => {
                const isActive = isChordActive(section.id, i);
                return (
                  <motion.div
                    key={i}
                    animate={{
                      scale: isActive ? 1.05 : 1,
                      boxShadow: isActive
                        ? chord.spice
                          ? "0 0 0 2px rgba(244,63,94,0.7)"
                          : "0 0 0 2px rgba(251,191,36,0.7)"
                        : "0 0 0 0 transparent",
                    }}
                    transition={{ duration: 0.1 }}
                    className={`flex flex-col items-center rounded-xl border px-2 py-2.5 text-center transition-colors ${
                      isActive
                        ? chord.spice
                          ? "border-rose-400/60 bg-rose-500/20"
                          : "border-amber-400/60 bg-amber-500/15"
                        : chord.spice
                        ? "border-rose-400/20 bg-rose-900/20"
                        : "border-zinc-700/50 bg-zinc-800/40"
                    }`}
                  >
                    <span
                      className={`font-mono text-[13px] font-bold ${
                        isActive
                          ? chord.spice
                            ? "text-rose-200"
                            : "text-amber-200"
                          : chord.spice
                          ? "text-rose-300/80"
                          : "text-cyan-100"
                      }`}
                    >
                      {chord.name}
                    </span>
                    {chord.spice && (
                      <span className="mt-0.5 text-[9px] font-bold uppercase tracking-wide text-rose-400/70">
                        spice
                      </span>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 pt-1 text-[11px]">
        <span className="flex items-center gap-1.5 text-cyan-300/60">
          <span className="inline-block h-3 w-3 rounded-sm border border-zinc-600 bg-zinc-800" />
          Diatonic chord
        </span>
        <span className="flex items-center gap-1.5 text-rose-300/70">
          <span className="inline-block h-3 w-3 rounded-sm border border-rose-400/40 bg-rose-900/30" />
          Spice chord (non-diatonic)
        </span>
        <span className="flex items-center gap-1.5 text-amber-300/70">
          <span className="inline-block h-3 w-3 rounded-sm border border-amber-400/50 bg-amber-500/15" />
          Active
        </span>
      </div>
    </div>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}
